/**
 * The banner a cost page shows when its total is incomplete: a usage type
 * with no rate row, or receipts with no exchange rate to convert them. A
 * missing figure looks exactly like something cheap, so each kind is named.
 */
import type { CostGaps } from '@optum/api-client/types';

function sentence(types: string[], gap: string): string | null {
  if (types.length === 0) return null;
  const n = types.length;
  return `${n} usage type${n === 1 ? '' : 's'} in this period ${n === 1 ? 'has' : 'have'} ${gap}: ${types.join(', ')}.`;
}

export function GapBanner({ gaps }: { gaps: CostGaps }) {
  const parts = [
    sentence(gaps.no_rate_types, 'no rate'),
    sentence(gaps.no_fx_types, 'receipts with no exchange rate'),
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) return null;
  return (
    <div
      role="alert"
      style={{
        borderRadius: 'var(--radius-control)',
        border: '1px solid var(--warn)',
        background: 'var(--warn-tint)',
        paddingLeft: 11,
        paddingRight: 11,
        paddingTop: 7,
        paddingBottom: 7,
        fontSize: 'var(--text-body-md)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--warn)',
      }}
    >
      {parts.join(' ')} Totals below are incomplete.
    </div>
  );
}

/** Amber chips naming each gap kind for one row of a table. */
export function GapChips({ gaps, Chip }: { gaps: CostGaps; Chip: GapChip }) {
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {gaps.no_rate_types.map((t) => (
        <Chip key={`rate-${t}`} label={`${t} · no rate`} />
      ))}
      {gaps.no_fx_types.map((t) => (
        <Chip key={`fx-${t}`} label={`${t} · no fx`} />
      ))}
    </span>
  );
}

export type GapChip = (props: { label: string }) => JSX.Element;

/** "99%" of a total, or "—" when there is no total to share. */
export function sharePercent(part: string, total: string): string {
  const p = Number(part);
  const t = Number(total);
  if (!Number.isFinite(p) || !Number.isFinite(t) || t === 0) return '—';
  return `${Math.round((p / t) * 100)}%`;
}
